import { useContext, useState } from "react";
import moment from "moment";
import { ChevronDown } from "untitledui-js-base";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { formatToCurrency } from "shared/utils";
import {
  CreditReportLatePayment,
  CreditReportLatePayments,
  CreditReportLatePaymentsError
} from "types/landlord/reports";
import { Section } from "views/applications-id-report/components/Section";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";
import { CreditStat } from "views/applications-id-report/slices/CreditReportSlice/components/AccountsSummary";

const VISIBLE_ROWS = 10;

const formatMonth = (month?: string | null) =>
  month ? moment(month, "YYYY-MM").format("MMM YYYY") : "-";

const Severity = ({ label }: { label: string }) => (
  <span className="text-danger-500 whitespace-nowrap">{label}</span>
);

export const LatePayments = ({
  latePayments,
  isNoHit,
  verificationId,
  onRowClick
}: {
  latePayments?:
    | CreditReportLatePayments
    | CreditReportLatePaymentsError
    | null;
  isNoHit: boolean;
  verificationId: number;
  onRowClick: (row: CreditReportLatePayment) => void;
}) => {
  const { printVerificationsState } = useContext(PrintAllContext);
  const [showAll, setShowAll] = useState(false);

  if (isNoHit || !latePayments)
    return (
      <Section sectionTitle="Late payments" maxWidth="100%">
        <p className="text-base font-medium text-content-300">
          No data available
        </p>
      </Section>
    );

  if ("error" in latePayments)
    return (
      <Section sectionTitle="Late payments" maxWidth="100%">
        <p className="text-base font-medium text-content-300">
          Late payments could not be loaded
        </p>
      </Section>
    );

  const isPrinting = String(verificationId) in printVerificationsState;
  const rows =
    showAll || isPrinting
      ? latePayments.rows
      : latePayments.rows.slice(0, VISIBLE_ROWS);

  return (
    <Section sectionTitle="Late payments" maxWidth="100%">
      <div className="grid grid-cols-4 gap-x-4 [&>*]:w-auto [&>*]:min-w-0">
        <CreditStat title="Late payments" value={String(latePayments.total)} />
        <CreditStat title="Accounts" value={String(latePayments.accounts)} />
        <CreditStat
          title="Most recent"
          value={formatMonth(latePayments.most_recent)}
        />
        <CreditStat
          title="Most severe"
          value={
            latePayments.worst ? <Severity label={latePayments.worst} /> : "-"
          }
        />
      </div>

      {latePayments.rows.length === 0 ? (
        <p className="mt-6 text-base font-medium text-content-1000">
          No late payments reported
        </p>
      ) : (
        <div className="mt-6 w-full">
          <div className="w-full border-b border-alpha-40 pb-2">
            <p className="text-sm leading-5 font-bold text-content-1000">
              Late payments history
            </p>
          </div>
          <div className="flex w-full items-center gap-2 border-b border-alpha-40 py-4 text-sm font-medium text-content-300">
            <p className="flex-1">Account</p>
            <p className="w-[130px] shrink-0">Month</p>
            <p className="w-[140px] shrink-0">How late</p>
            <p className="w-[120px] shrink-0">Status</p>
            <p className="w-[100px] shrink-0">Past due today</p>
            <span className="w-10 shrink-0" />
          </div>

          {rows.map((row) => (
            <button
              type="button"
              key={`${row.account_index}_${row.history_index}`}
              onClick={() => onRowClick(row)}
              className="group flex w-full items-center gap-2 border-b border-alpha-40 py-4 text-left hover:bg-alpha-20"
            >
              <div className="flex flex-1 min-w-0 items-center gap-2">
                <div className="min-w-0 max-w-[240px]">
                  <p className="text-sm font-medium text-content-1000 truncate">
                    {row.account_name || "--"}
                  </p>
                  <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs text-content-300">
                    {row.is_rental ? (
                      <Badge
                        variant={BadgeVariant.WARNING}
                        size={BadgeSize.SM}
                        className="shrink-0"
                      >
                        Rental
                      </Badge>
                    ) : (
                      <Show if={Boolean(row.account_type)}>
                        <span className="shrink-0 whitespace-nowrap">
                          {row.account_type} ·
                        </span>
                      </Show>
                    )}
                    <span className="min-w-0 truncate">
                      {row.account_number}
                    </span>
                  </div>
                </div>
              </div>
              <p className="w-[130px] shrink-0 text-sm font-medium text-content-1000">
                {formatMonth(row.month)}
              </p>
              <div className="w-[140px] shrink-0 text-sm font-medium">
                <Severity label={row.severity} />
              </div>
              <p className="w-[120px] shrink-0 text-sm font-medium text-content-1000">
                {row.account_status}
              </p>
              <p className="w-[100px] shrink-0 text-sm font-medium text-content-1000">
                {row.past_due_amount
                  ? formatToCurrency(row.past_due_amount)
                  : "$0.00"}
              </p>
              <span
                aria-hidden="true"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
              >
                <ChevronDown size="20px" className="stroke-content-1000" />
              </span>
            </button>
          ))}

          <Show if={latePayments.rows.length > VISIBLE_ROWS && !isPrinting}>
            <div className="flex justify-end pt-4">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                HugeIcon={showAll ? ArrowUp01Icon : ArrowDown01Icon}
                iconPosition="left"
                onClick={() => setShowAll((value) => !value)}
              >
                {showAll
                  ? "Show less"
                  : `Show all (${latePayments.rows.length})`}
              </Button>
            </div>
          </Show>
        </div>
      )}
    </Section>
  );
};
